import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Platform } from '@ionic/angular'
import camelCase from 'lodash.camelcase'
import snake_case from 'lodash.snakecase'
import packageJson from 'package.json'
import { Observable, throwError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { LocaleCode } from 'src/app/interfaces/locale'
import { HttpLoggingParams, LogEvent, LoginType } from 'src/app/interfaces/log'
import { LogService } from 'src/app/services/log/log.service'
import { customLocalStorage, isEmptyObject, isObject } from '../../../utils/utils'
import { RouterService } from '../../services/router/router.service'
import { LocaleService } from '../locale/locale-service'
import { SnackbarService } from './../snackbar/snackbar.service'

type httpErrorHandlerArgs = {
  err: HttpErrorResponse
  httpRequestOptions: HttpRequestOptions
  body?: any
}

// Api adapter
/**
 * JSON 형식의 데이터를 snake_case -> camelCase 로 전환해주는 어댑터입니다.
 */
export class ResponseAdapter {
  private transform<T, V>(targetObject: T | any | object): V | any {
    if (Array.isArray(targetObject)) {
      return (targetObject as T[]).map((value) => this.transform<T, V>(value)) as any
    } else if (!isObject(targetObject)) {
      return targetObject
    }

    const transformed: any = {}
    const keys = Object.keys(targetObject)

    for (const key of keys) {
      const value = targetObject[key]
      const transformedKey = camelCase(key)

      if (Array.isArray(value)) {
        transformed[transformedKey] = value.map((v) => this.transform(v))
      } else if (isObject(value)) {
        transformed[transformedKey] = this.transform(value)
      } else {
        transformed[transformedKey] = value
      }
    }

    return transformed as V
  }

  adapt<T, V>(response: T): V {
    return this.transform<T, V>(response)
  }
}

/**
 * firebase logging시 key값 하나만 snake로 바꾸는 경우 활용
 */
export function stringToSnakeCase(target: string): string {
  return snake_case(target)
}

/**
 * JSON 형식의 데이터를 camelCase -> snake_case 로 전환해주는 어댑터입니다.
 */
export class RequestAdapter {
  private transform<T, V>(targetObject: T | any | object): V | any {
    if (Array.isArray(targetObject)) {
      return (targetObject as T[]).map((value) => this.transform<T, V>(value)) as any
    } else if (!isObject(targetObject)) {
      return targetObject
    }

    const transformed: any = {}
    const keys = Object.keys(targetObject)

    for (const key of keys) {
      const value = targetObject[key]
      const transformedKey = snake_case(key)

      if (Array.isArray(value)) {
        transformed[transformedKey] = value.map((v) => this.transform(v))
      } else if (isObject(value)) {
        transformed[transformedKey] = this.transform(value)
      } else {
        transformed[transformedKey] = value
      }
    }

    return transformed as V
  }

  adapt<T, V>(requestBody: T): V {
    return this.transform<T, V>(requestBody)
  }
}

/**
 * 받은 query parameter를 url에 set 해주는 함수입니다.
 */
const setQueryParams = (
  url: string,
  queryParams: {
    [key: string]: string | number | string[] | number[] | boolean | null
  }
): string => {
  if (isEmptyObject(queryParams)) {
    return url
  }

  const requestAdapter = new RequestAdapter()
  queryParams = requestAdapter.adapt(queryParams)
  const paramList: any[] = []
  for (const key of Object.keys(queryParams)) {
    if (Array.isArray(queryParams[key])) {
      for (const value of queryParams[key] as Array<string | number | boolean>) {
        paramList.push(`${key}=${value}`)
      }
    } else if (
      queryParams[key] !== '' &&
      queryParams[key] !== 'null' &&
      queryParams[key] !== null &&
      queryParams[key] !== undefined &&
      queryParams[key] !== 'undefined'
    ) {
      paramList.push(`${key}=${queryParams[key]}`)
    }
  }
  if (paramList.length > 0) {
    url += '?' + paramList.join('&')
  }
  return url
}

@Injectable({
  providedIn: 'root',
})
export class CustomHttpClient {
  private token: string | null = null
  private version: string = packageJson.version

  header = new HttpHeaders()
  httpOptions: any = {
    headers: this.header,
  }

  constructor(
    private http: HttpClient,
    private platform: Platform,
    private routerService: RouterService,
    private requestAdapter: RequestAdapter,
    private responseAdapter: ResponseAdapter,
    private logService: LogService,
    private snackbarService: SnackbarService,
    private localeService: LocaleService
  ) {
    this.requestAdapter = new RequestAdapter()
    this.responseAdapter = new ResponseAdapter()
  }

  bearerToken(token: string): string {
    return `Bearer ${token}`
  }

  /**
   * 토큰을 헤더에 셋팅하는 함수입니다.
   * 토큰 만료는 빈 string (token='')으로 합니다.
   * token=null인 경우, 로그아웃 시 null.length undefined 요류가 발생할 수 있습니다.
   */
  setToken(token: string | null) {
    this.token = token
    if (this.token !== undefined && this.token !== '' && this.token !== null) {
      customLocalStorage.setItem('jwt', this.token)
      // todo: 이하 코드들 제거!!! 기존에 미처 못바꾼 부분때문에 냅둠.!
      this.header = new HttpHeaders().set('Authorization', this.bearerToken(this.token))
      this.httpOptions = {
        headers: this.header,
      }
    }
  }

  clearToken(): void {
    this.token = null
    customLocalStorage.removeItem('jwt')
    if (this.httpOptions.headers) {
      this.httpOptions.headers = this.header.delete('Authorization')
    }
  }

  // header에 jwt default로 세팅
  getRequestHeaders(
    options?:
      | HttpHeaders
      | {
          [header: string]: string | string[]
        }
  ): HttpHeaders {
    const headerOptions: any = {
      ...options,
    }
    if (this.token !== undefined && this.token !== '' && this.token !== null) {
      headerOptions.Authorization = this.bearerToken(this.token)
    } else if (customLocalStorage.getItem('jwt')) {
      this.setToken(customLocalStorage.getItem('jwt'))
      headerOptions.Authorization = this.bearerToken(this.token as string)
    }

    /* version 분기 필요해서 임시로 OS에 ios 넣음 */
    headerOptions['X-CLIENT-OS'] = 'ios'
    headerOptions['X-CLIENT-VERSION'] = this.version

    headerOptions['Accept-Language'] = this.localeService.locale
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .toLowerCase() as LocaleCode
    return new HttpHeaders(headerOptions)
  }

  get(url: string, options?: BlobHttpOptions): Observable<Blob>
  get<T>(url: string, options?: JsonHttpOptions): Observable<T>
  get(url: string, options?: HttpOptions): Observable<any> {
    if (options !== undefined && options.queryParams) {
      url = setQueryParams(url, options.queryParams)
      delete options.queryParams
    }

    const httpRequestOptions: HttpRequestOptions = this.getHttpRequestOptions(url, options)
    const request = this.http.get(url, httpRequestOptions)
    return this.sendRequest(request, httpRequestOptions.responseType)
  }

  post<T>(url: string, body?: any | null, options?: HttpOptions): Observable<T> {
    if (body === undefined) {
      body = {}
    }
    if (options !== undefined && options.queryParams) {
      url = setQueryParams(url, options.queryParams)
      delete options.queryParams
    }

    const httpRequestOptions: HttpRequestOptions = this.getHttpRequestOptions(url, options)

    let request: Observable<any>
    if (body instanceof FormData) {
      request = this.http.post(url, body, httpRequestOptions)
    } else {
      request = this.http.post(url, this.requestAdapter.adapt(body), httpRequestOptions)
    }

    const loggingOptions: HttpLoggingParams = {
      method: 'post',
      callers: this.callerNames(),
      httpRequestOptions,
      body,
      apiUrl: url,
      loggingText: options?.loggingText,
    }

    return this.sendRequest(request, httpRequestOptions.responseType, loggingOptions).pipe(
      tap((res) => {
        // init, connect api는 로깅하지 않음
        if (url.includes('/init') || url.includes('/connect')) return
        this.logService.eventLogging(LogEvent.CONTENT_CREATED, { url: loggingOptions?.apiUrl })
      })
    )
  }

  patch<T>(url: string, body: any | null, options?: HttpOptions): Observable<T> {
    if (body === undefined) {
      body = {}
    }
    if (options !== undefined && options.queryParams) {
      url = setQueryParams(url, options.queryParams)
      delete options.queryParams
    }

    const httpRequestOptions: HttpRequestOptions = this.getHttpRequestOptions(url, options)

    let request: Observable<any>
    if (body instanceof FormData) {
      request = this.http.patch(url, body, httpRequestOptions)
    } else {
      request = this.http.patch(url, this.requestAdapter.adapt(body), httpRequestOptions)
    }

    const loggingOptions: HttpLoggingParams = {
      method: 'patch',
      callers: this.callerNames(),
      httpRequestOptions,
      body,
      apiUrl: url,
      loggingText: options?.loggingText,
    }
    return this.sendRequest(request, httpRequestOptions.responseType, loggingOptions)
  }

  delete<T>(url: string, options?: HttpOptions): Observable<T> {
    if (options !== undefined && options.queryParams) {
      url = setQueryParams(url, options.queryParams)
      delete options.queryParams
    }

    const httpRequestOptions: HttpRequestOptions = this.getHttpRequestOptions(url, options)

    const request = this.http.delete(url, httpRequestOptions)

    const loggingOptions: HttpLoggingParams = {
      method: 'delete',
      callers: this.callerNames(),
      httpRequestOptions,
      apiUrl: url,
      loggingText: options?.loggingText,
    }
    return this.sendRequest(request, httpRequestOptions.responseType, loggingOptions)
  }

  // httpRequestOptions 가져오기
  getHttpRequestOptions(url: string, options: HttpOptions | undefined): HttpRequestOptions {
    let httpRequestOptions: HttpRequestOptions = {}

    // set options
    if (options !== undefined) {
      httpRequestOptions = {
        ...options,
      }
    }
    // set headers
    if (options !== undefined && options.headers !== undefined) {
      httpRequestOptions.headers = this.getRequestHeaders(options.headers)
    } else {
      httpRequestOptions.headers = this.getRequestHeaders()
    }

    // set responseType
    if (options && options.responseType !== undefined) {
      httpRequestOptions.responseType = options.responseType
    }

    // get response header
    httpRequestOptions.observe = 'response'

    return httpRequestOptions
  }

  // request 보내기
  sendRequest(request: Observable<any>, responseType: string, loggingOptions?: HttpLoggingParams): Observable<any> {
    /**
     * blob 일 경우는 adapter 안쓰기
     * 다른 경우 있을 경우 케이스별로 그때 상황에 맞게 수정 요망
     */
    if (responseType === 'blob') {
      return request
    } else {
      return request.pipe(
        tap((res: HttpResponse<any>) => {
          const statusCode: number = res.status
          if (loggingOptions !== undefined) {
            this.logService.httpLogging({
              ...loggingOptions,
              statusCode,
            })
          }
          const message: string = res.body ? res.body.message : ''
          this.handleResponseStatus(statusCode, message)
        }),
        map((res: HttpResponse<any>) => {
          return this.responseAdapter.adapt(res.body)
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          const { status, error } = errorResponse
          this.handleResponseStatus(status, error)
          if (loggingOptions !== undefined) {
            this.logService.httpLogging({
              ...loggingOptions,
              statusCode: status,
              errorMessage: error.message ?? '',
            })
          }
          return throwError(errorResponse)
        })
      )
    }
  }

  // statusCode에 따른 일괄 처리
  // TODO: 에러시 처리 형태 정하기 + 구현
  handleResponseStatus(statusCode: number, error: any): void {
    const message = error?.message ?? ''
    const code = error?.code
    switch (statusCode) {
      case 200: // OK
        break
      case 201: // Created
        break
      case 204: // No Content
        break
      case 400: // Bad Request
        // this.snackbarService.showSnackbar({ message });
        break
      case 401: // Unauthoirzed
        this.platform.ready().then(async () => {
          this.clearToken()
          this.routerService.navigate('login', { queryParams: { type: LoginType.UNAUTHORIZED } })
        })
        // this.snackbarService.showSnackbar({ message });
        break
      case 403: // Forbidden
        // 이미 탈퇴한 유저의 경우
        if (code === 'USER_ALREADY_WITHDREW') {
          this.snackbarService.openSnackBar(SnackbarService.ALREADY_WITHDREW)
          this.clearToken()
          this.routerService.navigate('login', { queryParams: { type: LoginType.WITHDRAWN_USER } })
        }
        // this.snackbarService.showSnackbar({ message });
        // this.routerService.navigateToErrorPage(statusCode)
        break
      case 404: // Not Found
        // this.snackbarService.showSnackbar({ message });
        // this.routerService.navigateToErrorPage(statusCode)
        break
      case 408: // Request Timeout
        // this.snackbarService.showSnackbar({ message });
        this.routerService.navigateToErrorPage(statusCode)
        break
      default:
        this.routerService.navigateToErrorPage(statusCode)
    }
  }

  private callerNames(): string[] {
    const re = /(at )[a-zA-Z-.]*(CustomHttpClient|Component|Service|Pipe|Guard|Directive).[a-zA-Z]+/gi
    const errorStack = new Error().stack
    if (errorStack !== undefined) {
      const aRegexResult = errorStack.match(re)
      if (aRegexResult !== null) {
        return aRegexResult.slice(2).map((res) => res.replace('at ', ''))
      }
    }
    return []
  }
}

// 실제로 서버 요청에 넣을 options
export interface HttpRequestOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[]
      }
  observe?: any
  params?:
    | HttpParams
    | {
        [param: string]: string | string[]
      }
  reportProgress?: boolean
  responseType?: any
  withCredentials?: boolean
}

// service에서 전달되는 options
interface HttpOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[]
      }
  queryParams?: {
    [key: string]: string | number | string[] | number[] | boolean | null
  }
  responseType?: any
  requestType?: any
  loggingText?: string //  just for logging
}

interface BlobHttpOptions extends HttpOptions {
  responseType?: 'blob'
}

interface JsonHttpOptions extends HttpOptions {
  responseType?: 'json'
}
